import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type SearchResult = {
  id: string;
  type: 'organization' | 'contact' | 'opportunity' | 'task' | 'activity';
  title: string;
  subtitle?: string;
  url: string;
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSearch = async (value: string) => {
    setQuery(value);

    if (!value || value.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);

    try {
      // Search organizations
      const { data: organizations, error: orgError } = await supabase
        .from('organizations')
        .select('id, name, description')
        .or(`name.ilike.%${value}%, description.ilike.%${value}%`)
        .limit(3);
        
      if (orgError && orgError.code !== 'PGRST116') {
        console.error('Error searching organizations:', orgError);
      }

      // Search contacts
      const { data: contacts, error: contactError } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, organization_id, organizations(name)')
        .or(`first_name.ilike.%${value}%, last_name.ilike.%${value}%, email.ilike.%${value}%`)
        .limit(3);
        
      if (contactError && contactError.code !== 'PGRST116') {
        console.error('Error searching contacts:', contactError);
      }

      // Search opportunities
      const { data: opportunities, error: oppError } = await supabase
        .from('opportunities')
        .select('id, name, organization_id, organizations(name)')
        .or(`name.ilike.%${value}%`)
        .limit(3);
        
      if (oppError && oppError.code !== 'PGRST116') {
        console.error('Error searching opportunities:', oppError);
      }

      // Search tasks
      const { data: tasks, error: taskError } = await supabase
        .from('tasks')
        .select('id, title')
        .or(`title.ilike.%${value}%`)
        .limit(3);
        
      if (taskError && taskError.code !== 'PGRST116') {
        console.error('Error searching tasks:', taskError);
      }

      // Format results
      const formattedResults: SearchResult[] = [
        ...(organizations || []).map((org) => ({
          id: org.id,
          type: 'organization' as const,
          title: org.name,
          subtitle: org.description,
          url: `/organizations/${org.id}`,
        })),
        ...(contacts || []).map((contact) => ({
          id: contact.id,
          type: 'contact' as const,
          title: `${contact.first_name} ${contact.last_name}`,
          subtitle: contact.organizations?.name,
          url: `/contacts/${contact.id}`,
        })),
        ...(opportunities || []).map((opportunity) => ({
          id: opportunity.id,
          type: 'opportunity' as const,
          title: opportunity.name,
          subtitle: opportunity.organizations?.name,
          url: `/opportunities/${opportunity.id}`,
        })),
        ...(tasks || []).map((task) => ({
          id: task.id,
          type: 'task' as const,
          title: task.title,
          url: `/tasks/${task.id}`,
        })),
      ];

      setResults(formattedResults);
      
      if (formattedResults.length === 0) {
        console.log('No search results found for:', value);
      }
    } catch (error) {
      console.error('Global search error:', error);
      setResults([]); // Clear results on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (item: SearchResult) => {
    setOpen(false);
    navigate(item.url);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'organization':
        return '🏢';
      case 'contact':
        return '👤';
      case 'opportunity':
        return '🎯';
      case 'task':
        return '📋';
      case 'activity':
        return '📅';
      default:
        return '📄';
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="gap-2 h-9 w-full justify-between md:w-40 lg:w-64"
        onClick={() => setOpen(true)}
      >
        <div className="flex items-center">
          <Search className="mr-2 h-4 w-4" />
          <span>Search...</span>
        </div>
        <kbd className="hidden pointer-events-none text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded md:inline-flex items-center gap-1">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search organizations, contacts, tasks..."
          value={query}
          onValueChange={handleSearch}
        />
        <CommandList>
          {isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Searching...</div>
          ) : (
            <>
              <CommandEmpty>No results found.</CommandEmpty>
              {results.length > 0 && (
                <>
                  <CommandGroup heading="Organizations">
                    {results
                      .filter((item) => item.type === 'organization')
                      .map((item) => (
                        <CommandItem key={item.id} onSelect={() => handleSelect(item)}>
                          <div className="flex items-center">
                            <span className="mr-2">{getTypeIcon(item.type)}</span>
                            <div>
                              <div>{item.title}</div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>

                  <CommandGroup heading="Contacts">
                    {results
                      .filter((item) => item.type === 'contact')
                      .map((item) => (
                        <CommandItem key={item.id} onSelect={() => handleSelect(item)}>
                          <div className="flex items-center">
                            <span className="mr-2">{getTypeIcon(item.type)}</span>
                            <div>
                              <div>{item.title}</div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>

                  <CommandGroup heading="Opportunities">
                    {results
                      .filter((item) => item.type === 'opportunity')
                      .map((item) => (
                        <CommandItem key={item.id} onSelect={() => handleSelect(item)}>
                          <div className="flex items-center">
                            <span className="mr-2">{getTypeIcon(item.type)}</span>
                            <div>
                              <div>{item.title}</div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>

                  <CommandGroup heading="Tasks">
                    {results
                      .filter((item) => item.type === 'task')
                      .map((item) => (
                        <CommandItem key={item.id} onSelect={() => handleSelect(item)}>
                          <div className="flex items-center">
                            <span className="mr-2">{getTypeIcon(item.type)}</span>
                            <div>
                              <div>{item.title}</div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </>
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
